import { useDialog } from "../hooks/useDialog";
import { navigateTabs } from "../lib/tabNavigation";
import { memo, useRef, useMemo, useState } from "react";
import type { IncidentFeature, IncidentAuditEntry } from "@chula/shared";
import { IncidentReplyForm } from "./IncidentReplyForm";
import { IncidentAuditThread } from "./IncidentAuditThread";
import { IncidentEvidence } from "./IncidentEvidence";
import { operatorFetch } from "../lib/operatorToken";
import { langOf } from "../lib/lang";

/**
 * IncidentCard — issue-card UI for a clicked city-report dot.
 *
 * What it does that Traffy can't:
 *   • Names the owner from the start (staffName + teamName, single row),
 *     so the "musical chairs of responsibility erosion" doesn't have
 *     anywhere to start.
 *   • Renders the audit_log as a categorized timeline. Each staff_message
 *     carries a self-declared kind: acknowledge / request / inform /
 *     completed / blocked — color-coded so an admin scanning the thread
 *     sees the conversational shape in one glance.
 *   • Two views:
 *        INTERNAL  — full thread between admin + staff (default for ops)
 *        CITIZEN   — filtered to status transitions + replies that were
 *                    actually pushed to the citizen's LINE, so the
 *                    operator can verify what the reporter has seen.
 *   • Magic Eye AI summary surfaces above the description so the
 *     classification is auditable.
 *   • Before/after photos side-by-side once solutionImageUrl is set.
 *
 * Response notes: the LOG RESPONSE NOTE form POSTs to
 * /api/incidents/city-reports/:id/reply, which is an INTERNAL audit-note
 * store. Nothing is pushed to the citizen — the Traffy bot exposes no API
 * for dashboard-originated messages. The copy below is deliberately honest
 * about that; revisit when a real citizen-messaging integration lands.
 */

interface Props {
  incident: IncidentFeature | null;
  onClose: () => void;
  /** API base URL — same as the rest of the app pulls from. */
  apiBase: string;
  /** Called after a successful reply so the parent can refetch. */
  onReplied?: () => void;
}

type View = "internal" | "citizen";

/** Lifecycle statuses accepted by POST /api/incidents/campus-reports/:id/status. */
type CampusLifecycleStatus = "active" | "in_progress" | "fixed" | "archived";

const CAMPUS_CONFIRM_LABEL: Record<"fixed" | "archived", string> = {
  fixed:    "FIXED — moved to archive roll",
  archived: "ARCHIVED — left active tasks",
};

/** Preset responsible teams — free text still allowed via the "Other…" row. */
const TEAM_PRESETS = [
  "Facilities · อาคารสถานที่",
  "Grounds & Landscaping · ภูมิทัศน์",
  "Electrical · ไฟฟ้า",
  "Plumbing & Drainage · ประปา",
  "Cleaning · แม่บ้าน",
  "Security · รปภ.",
  "IT & Network",
  "Traffic & Transport · จราจร",
];

/** Kinds accepted by POST /api/incidents/city-reports/:id/reply (internal audit note). */
const REPLY_KINDS: Array<{ id: string; label: string; color: string }> = [
  { id: "acknowledge",  label: "ACK",       color: "var(--data)" },
  { id: "request_info", label: "REQUEST",   color: "var(--accent)" },
  { id: "progress",     label: "PROGRESS",  color: "var(--warn)" },
  { id: "completed",    label: "COMPLETED", color: "var(--good)" },
  { id: "note",         label: "NOTE",      color: "var(--text-2)" },
];

const STATUS_COLOR: Record<string, string> = {
  received:      "var(--text-3)",
  assigned:      "var(--data)",
  "in-progress": "var(--warn)",
  resolved:      "var(--good)",
};

const STATUS_LABEL: Record<string, string> = {
  received:      "RECEIVED",
  assigned:      "ASSIGNED",
  "in-progress": "IN PROGRESS",
  resolved:      "RESOLVED",
};

const SEVERITY_COLOR: Record<string, string> = {
  low:      "var(--text-3)",
  medium:   "var(--data)",
  high:     "var(--warn)",
  critical: "var(--bad)",
};

function fmtAgo(iso: string | undefined): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "—";
  const min = Math.round(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  if (min < 60 * 24) return `${Math.round(min / 60)}h ago`;
  return `${Math.round(min / (60 * 24))}d ago`;
}

function isCitizenVisible(entry: IncidentAuditEntry): boolean {
  // Status changes are always visible.
  if (entry.type === "status_change") return true;
  // Staff messages are only visible if delivered to LINE.
  if (entry.type === "staff_message") return entry.delivered === true;
  // Citizen replies — also surfaced.
  if (entry.type === "citizen_reply") return true;
  // AI findings + category locks stay internal.
  return false;
}

function IncidentCardImpl({ incident, onClose, apiBase, onReplied }: Props) {
  const dialogRef = useRef<HTMLElement>(null);
  useDialog(dialogRef, !!incident, onClose, false);
  const [view, setView] = useState<View>("internal");
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyKind, setReplyKind] = useState<string>("acknowledge");
  const [replyText, setReplyText] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  // Campus-report (Telegram) lifecycle — MARK FIXED / ARCHIVE operator actions.
  const [lifecycleBusy, setLifecycleBusy] = useState<CampusLifecycleStatus | null>(null);
  // Only terminal states show a confirmation banner — in_progress keeps the card open.
  const [lifecycleDone, setLifecycleDone] = useState<"fixed" | "archived" | null>(null);
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);
  // Two-step confirm flow — first click arms, second click commits. Prevents
  // one-tap mis-fires on a public dashboard (Don Norman: force the operator
  // to confirm destructive intent).
  const [lifecycleArmed, setLifecycleArmed] = useState<CampusLifecycleStatus | null>(null);
  // Telegram reply form — sends a message back to the original reporter
  // (in-group + DM where possible). Separate from the Traffy "LOG RESPONSE
  // NOTE" path; that one is an internal audit log that never reaches anyone.
  const [replyTgOpen, setReplyTgOpen] = useState(false);
  const [replyTgText, setReplyTgText] = useState("");
  const [replyTgBusy, setReplyTgBusy] = useState(false);
  const [replyTgError, setReplyTgError] = useState<string | null>(null);
  const [replyTgDone, setReplyTgDone] = useState<{ inGroupSent: boolean; dmSent: boolean } | null>(null);
  // Accept & assign — the primary "someone owns this now" action. Names the
  // responsible team (preset or free text) + an optional first note, then
  // pushes "Team X has accepted your report" back to the citizen.
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTeam, setAssignTeam] = useState<string>(TEAM_PRESETS[0]);
  const [assignCustom, setAssignCustom] = useState("");
  const [assignNote, setAssignNote] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  async function submitAssign() {
    if (!incident) return;
    const team = (assignTeam === "__other__" ? assignCustom : assignTeam).trim();
    if (!team) {
      setAssignError("Pick or type a team");
      return;
    }
    setAssignBusy(true);
    setAssignError(null);
    try {
      const res = await operatorFetch(
        `${apiBase}/api/incidents/campus-reports/${encodeURIComponent(incident.id)}/assign`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ team, note: assignNote.trim() || undefined, by: "dashboard-operator" }),
        },
      );
      const json = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string };
      if (!res.ok || !json.success) {
        setAssignError(json.error ?? `Assign failed (${res.status})`);
      } else {
        setAssignOpen(false);
        setAssignNote("");
        onReplied?.();
      }
    } catch (err) {
      setAssignError(String((err as Error).message ?? err));
    } finally {
      setAssignBusy(false);
    }
  }

  async function updateCampusStatus(status: CampusLifecycleStatus) {
    if (!incident) return;
    // Two-step confirm only for destructive closes (fixed / archived).
    const needsArm = status === "fixed" || status === "archived";
    if (needsArm && lifecycleArmed !== status) {
      setLifecycleArmed(status);
      setLifecycleError(null);
      return;
    }
    setLifecycleBusy(status);
    setLifecycleError(null);
    try {
      const res = await operatorFetch(
        `${apiBase}/api/incidents/campus-reports/${encodeURIComponent(incident.id)}/status`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ status, by: "dashboard-operator" }),
        },
      );
      const json = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string };
      if (!res.ok || !json.success) {
        setLifecycleError(json.error ?? `Update failed (${res.status})`);
        setLifecycleArmed(null);
      } else {
        if (status === "fixed" || status === "archived") setLifecycleDone(status);
        setLifecycleArmed(null);
        onReplied?.();
      }
    } catch (err) {
      setLifecycleError(String((err as Error).message ?? err));
      setLifecycleArmed(null);
    } finally {
      setLifecycleBusy(null);
    }
  }

  async function sendCampusLog() {
    if (!incident) return;
    const text = replyText.trim();
    if (!text) {
      setReplyError("Progress note can't be empty");
      return;
    }
    setReplyBusy(true);
    setReplyError(null);
    try {
      const res = await operatorFetch(
        `${apiBase}/api/incidents/campus-reports/${encodeURIComponent(incident.id)}/log`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            text,
            kind: replyKind === "request_info" ? "request_info" : replyKind,
            by: "Chula Ops",
          }),
        },
      );
      const json = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string };
      if (!res.ok || !json.success) {
        setReplyError(json.error ?? `Log failed (${res.status})`);
      } else {
        setReplyText("");
        setReplyOpen(false);
        onReplied?.();
      }
    } catch (err) {
      setReplyError(String((err as Error).message ?? err));
    } finally {
      setReplyBusy(false);
    }
  }

  async function sendTelegramReply() {
    if (!incident) return;
    const text = replyTgText.trim();
    if (!text) {
      setReplyTgError("Message can't be empty");
      return;
    }
    setReplyTgBusy(true);
    setReplyTgError(null);
    try {
      const res = await operatorFetch(
        `${apiBase}/api/incidents/campus-reports/${encodeURIComponent(incident.id)}/reply`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ text, by: "Chula Ops" }),
        },
      );
      const json = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        error?: string;
        delivered?: { inGroupSent: boolean; dmSent: boolean };
      };
      if (!res.ok || !json.success) {
        setReplyTgError(json.error ?? `Send failed (${res.status})`);
      } else {
        setReplyTgDone(json.delivered ?? { inGroupSent: false, dmSent: false });
        setReplyTgText("");
        setReplyTgOpen(false);
        onReplied?.();
      }
    } catch (err) {
      setReplyTgError(String((err as Error).message ?? err));
    } finally {
      setReplyTgBusy(false);
    }
  }

  async function sendReply() {
    if (!incident) return;
    const text = replyText.trim();
    if (!text) {
      setReplyError("Message can't be empty");
      return;
    }
    setReplyBusy(true);
    setReplyError(null);
    try {
      const res = await operatorFetch(`${apiBase}/api/incidents/city-reports/${encodeURIComponent(incident.id)}/reply`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          text,
          kind: replyKind,
          staffName: "Chula Admin",
        }),
      });
      const json = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string; delivered?: boolean };
      if (!res.ok || !json.success) {
        setReplyError(json.error ?? `Send failed (${res.status})`);
      } else {
        setReplyText("");
        setReplyOpen(false);
        onReplied?.();
      }
    } catch (err) {
      setReplyError(String((err as Error).message ?? err));
    } finally {
      setReplyBusy(false);
    }
  }

  // Hooks must run unconditionally — compute thread + status timeline
  // even when there's no incident, then bail in the render.
  const audit = useMemo(() => incident?.auditLog ?? [], [incident]);
  const visibleEntries = useMemo(
    () => (view === "internal" ? audit : audit.filter(isCitizenVisible)),
    [audit, view],
  );

  const statusTimeline = useMemo(() => {
    const order: IncidentFeature["status"][] = ["received", "assigned", "in-progress", "resolved"];
    const reached = new Set<string>(["received"]);
    for (const e of audit) {
      if (e.type === "status_change" && e.to) reached.add(e.to);
    }
    if (incident?.status) reached.add(incident.status);
    return order.map((s) => ({ status: s, reached: reached.has(s) }));
  }, [incident, audit]);

  if (!incident) return null;

  const severityColor = SEVERITY_COLOR[incident.severity] ?? "var(--text-2)";
  const statusColor = STATUS_COLOR[incident.status] ?? "var(--text-3)";
  // Telegram campus reports (id "tg-…") support the operator lifecycle:
  // MARK FIXED / ARCHIVE remove them from the active feed server-side.
  const isCampusReport = incident.id.startsWith("tg-");
  // "Accepted" = a team owns it (in-progress / resolved, or a team is named).
  // Gates the guided flow: unaccepted tickets lead with ACCEPT & ASSIGN;
  // accepted ones lead with MESSAGE REPORTER + MARK FIXED.
  const accepted =
    isCampusReport &&
    (incident.status === "in-progress" || incident.status === "resolved" || !!incident.teamName);
  const ratingNum = incident.rating ? Math.round(Number(incident.rating)) : null;

  return (
    <aside
      ref={dialogRef}
      className="incident-card"
      role="dialog"
      aria-label={`Incident ${incident.ticketNumber ?? incident.id}`}
    >
      <header className="incident-card-head">
        <div className="incident-card-id">
          <span className="eyebrow mono">CR · {incident.ticketNumber ?? incident.id.slice(-6)}</span>
          <h3 className="incident-card-title">{incident.title}</h3>
        </div>
        <button
          onClick={onClose}
          className="incident-card-close mono"
          aria-label="Close incident card"
          title="Close (Esc)"
        >
          [ESC]
        </button>
      </header>

      {/* Pills row: status · severity · category · reported-ago */}
      <div className="incident-pills">
        <span className="incident-pill mono" style={{ borderColor: statusColor, color: statusColor }}>
          {STATUS_LABEL[incident.status] ?? incident.status.toUpperCase()}
        </span>
        <span className="incident-pill mono" style={{ borderColor: severityColor, color: severityColor }}>
          {incident.severity.toUpperCase()}
        </span>
        <span className="incident-pill mono">
          {(incident.categoryLocked || incident.category).toUpperCase()}
        </span>
        <span className="incident-pill incident-pill-ghost mono">
          {fmtAgo(incident.reportedAt)}
        </span>
      </div>

      {/* Status timeline — no musical chairs */}
      <ol className="incident-timeline" aria-label="Status timeline">
        {statusTimeline.map(({ status, reached }, i) => (
          <li
            key={status}
            className={`incident-tl-node ${reached ? "reached" : "pending"} ${incident.status === status ? "current" : ""}`}
          >
            <span className="incident-tl-dot" style={{ background: reached ? STATUS_COLOR[status] : "var(--line)" }} />
            <span className="incident-tl-label mono">{STATUS_LABEL[status]}</span>
            {i < statusTimeline.length - 1 && (
              <span
                className="incident-tl-line"
                style={{ background: statusTimeline[i + 1].reached ? STATUS_COLOR[statusTimeline[i + 1].status] : "var(--line)" }}
              />
            )}
          </li>
        ))}
      </ol>

      <IncidentEvidence
        apiBase={apiBase}
        imageUrl={incident.imageUrl}
        solutionImageUrl={incident.solutionImageUrl}
        title={incident.title}
        lat={incident.lat}
        lng={incident.lng}
      />

      {/* Owner — named, single row, no committee */}
      <div className="incident-owner">
        <div className="incident-owner-row">
          <span className="eyebrow mono">Owner</span>
          <span className="incident-owner-name">
            {incident.teamName
              ? <span className="incident-owner-team-name">{incident.teamName}</span>
              : incident.staffName
                ? incident.staffName
                : <span style={{ color: "var(--text-3)" }}>unassigned</span>}
          </span>
        </div>
        {incident.staffComment && (
          <p className="incident-owner-comment">{incident.staffComment}</p>
        )}
        {ratingNum !== null && (
          <div className="incident-rating" title={`Citizen rated the fix ${ratingNum} of 5`}>
            <span className="eyebrow mono">Citizen rating</span>
            <span className="incident-rating-stars" aria-label={`${ratingNum} out of 5 stars`}>
              {"★".repeat(ratingNum)}<span className="incident-rating-empty">{"★".repeat(5 - ratingNum)}</span>
            </span>
            <span className="incident-rating-num mono">{ratingNum}/5</span>
          </div>
        )}
      </div>

      {/* Description from citizen */}
      {incident.description && (
        <div className="incident-desc">
          <span className="eyebrow mono">Citizen description</span>
          <p lang={langOf(incident.description)}>{incident.description}</p>
          {incident.nickname && (
            <span className="caption mono incident-reporter">— {incident.nickname}</span>
          )}
        </div>
      )}

      {/* AI assists after the operator has seen the primary human evidence. */}
      {incident.aiSummary && (
        <div className="incident-ai">
          <span className="eyebrow mono">Magic Eye · AI verdict</span>
          <p className="incident-ai-body">{incident.aiSummary}</p>
        </div>
      )}

      {/* Internal/Citizen view toggle */}
      <div className="incident-tabs" role="tablist" tabIndex={-1} onKeyDown={navigateTabs} aria-label="Thread view">
        <button
          role="tab"
          aria-selected={view === "internal"}
          tabIndex={view === "internal" ? 0 : -1}
          className={`incident-tab mono ${view === "internal" ? "active" : ""}`}
          onClick={() => setView("internal")}
        >
          INTERNAL · {audit.length}
        </button>
        <button
          role="tab"
          aria-selected={view === "citizen"}
          tabIndex={view === "citizen" ? 0 : -1}
          className={`incident-tab mono ${view === "citizen" ? "active" : ""}`}
          onClick={() => setView("citizen")}
        >
          CITIZEN VIEW · {audit.filter(isCitizenVisible).length}
        </button>
      </div>

      <IncidentAuditThread
        entries={visibleEntries}
        emptyMessage={
          view === "internal"
            ? "No internal messages yet. Once staff acknowledges or updates the ticket, the conversation lands here."
            : "Citizen has not yet seen any update beyond the initial receipt. Once an admin reply lands on LINE, it appears here."
        }
      />

      {/* Campus-report operator actions — guided task lifecycle */}
      {isCampusReport && (
        <div className="campus-report-actions">
          {lifecycleDone ? (
            <span className="campus-report-confirm mono" role="status">
              {CAMPUS_CONFIRM_LABEL[lifecycleDone]}
            </span>
          ) : (
            <>
              {/* Guided step header — tells the operator what to do next */}
              <span className="eyebrow mono campus-step-label">
                {accepted ? "Step 2 · Work it, keep the reporter posted, then close" : "Step 1 · Accept & assign a team"}
              </span>

              {/* ── STEP 1: ACCEPT & ASSIGN (unaccepted tickets) ─────────── */}
              {!accepted && (
                assignOpen ? (
                  <div className="campus-assign-form">
                    <label className="eyebrow mono" htmlFor="assign-team">Responsible team</label>
                    <select
                      id="assign-team"
                      className="campus-assign-select mono"
                      value={assignTeam}
                      onChange={(e) => setAssignTeam(e.target.value)}
                    >
                      {TEAM_PRESETS.map((t) => <option key={t} value={t}>{t}</option>)}
                      <option value="__other__">Other… (type below)</option>
                    </select>
                    {assignTeam === "__other__" && (
                      <input
                        className="campus-assign-input mono"
                        placeholder="Team / department name"
                        aria-label="Custom team name"
                        value={assignCustom}
                        maxLength={80}
                        onChange={(e) => setAssignCustom(e.target.value)}
                      />
                    )}
                    <label className="eyebrow mono" htmlFor="assign-note">Note to reporter (optional)</label>
                    <textarea
                      id="assign-note"
                      className="campus-assign-note mono"
                      placeholder="e.g. A crew is heading over this afternoon — the citizen sees this."
                      value={assignNote}
                      onChange={(e) => setAssignNote(e.target.value)}
                      maxLength={300}
                      rows={2}
                    />
                    <div className="campus-report-reply-row">
                      <button
                        className="mono campus-report-btn campus-report-btn--accept"
                        disabled={assignBusy}
                        onClick={submitAssign}
                      >
                        {assignBusy ? "ACCEPTING…" : "ACCEPT & NOTIFY REPORTER ▶"}
                      </button>
                      <button
                        className="mono campus-report-btn campus-report-btn--cancel"
                        disabled={assignBusy}
                        onClick={() => { setAssignOpen(false); setAssignError(null); }}
                      >
                        CANCEL
                      </button>
                    </div>
                    {assignError && (
                      <span className="incident-reply-error campus-report-error mono" role="alert">{assignError}</span>
                    )}
                  </div>
                ) : (
                  <button
                    className="mono campus-report-btn campus-report-btn--accept campus-report-btn--primary"
                    onClick={() => { setAssignOpen(true); setAssignError(null); }}
                    title="Take the ticket, name the responsible team, and tell the reporter you're on it"
                  >
                    ✋ ACCEPT & ASSIGN TEAM ▶
                  </button>
                )
              )}

              {/* ── STEP 2: work it + close (accepted tickets) ───────────── */}
              {accepted && (
                <div className="campus-report-workflow">
                  {lifecycleArmed && (
                    <span className="campus-report-armed mono" role="status">
                      click again to {lifecycleArmed === "fixed" ? "mark fixed" : "archive"} ·{" "}
                      <button
                        className="campus-report-cancel mono"
                        onClick={() => setLifecycleArmed(null)}
                        aria-label="Cancel operator action"
                      >
                        cancel
                      </button>
                    </span>
                  )}
                  {incident.status !== "resolved" ? (
                    <>
                      <button
                        className={`mono campus-report-btn campus-report-btn--fix${lifecycleArmed === "fixed" ? " armed" : ""}`}
                        disabled={lifecycleBusy !== null}
                        onClick={() => updateCampusStatus("fixed")}
                        title="Mark fixed — notifies the reporter + asks them to rate 1–5; leaves the map"
                      >
                        {lifecycleBusy === "fixed"
                          ? "MARKING…"
                          : lifecycleArmed === "fixed"
                            ? "CONFIRM FIX ▶"
                            : "✓ MARK FIXED ▶"}
                      </button>
                      <button
                        className={`mono campus-report-btn campus-report-btn--archive${lifecycleArmed === "archived" ? " armed" : ""}`}
                        disabled={lifecycleBusy !== null}
                        onClick={() => updateCampusStatus("archived")}
                        title="Archive without marking fixed — two-click confirm"
                      >
                        {lifecycleBusy === "archived"
                          ? "ARCHIVING…"
                          : lifecycleArmed === "archived"
                            ? "CONFIRM ARCHIVE ▶"
                            : "ARCHIVE"}
                      </button>
                      <button
                        className="mono campus-report-reassign"
                        onClick={() => { setAssignOpen(true); setAssignError(null); }}
                        title="Hand the ticket to a different team"
                      >
                        reassign team
                      </button>
                    </>
                  ) : (
                    <button
                      className="mono campus-report-btn campus-report-btn--work"
                      disabled={lifecycleBusy !== null}
                      onClick={() => updateCampusStatus("in_progress")}
                      title="Reopen into active tasks"
                    >
                      {lifecycleBusy === "in_progress" ? "REOPENING…" : "↩ REOPEN ▶"}
                    </button>
                  )}
                  {/* reassign form when opened from an accepted ticket */}
                  {assignOpen && (
                    <div className="campus-assign-form">
                      <label className="eyebrow mono" htmlFor="reassign-team">Reassign to</label>
                      <select
                        id="reassign-team"
                        className="campus-assign-select mono"
                        value={assignTeam}
                        onChange={(e) => setAssignTeam(e.target.value)}
                      >
                        {TEAM_PRESETS.map((t) => <option key={t} value={t}>{t}</option>)}
                        <option value="__other__">Other…</option>
                      </select>
                      {assignTeam === "__other__" && (
                        <input
                          className="campus-assign-input mono"
                          placeholder="Team / department name"
                          aria-label="Custom team name"
                          value={assignCustom}
                          maxLength={80}
                          onChange={(e) => setAssignCustom(e.target.value)}
                        />
                      )}
                      <div className="campus-report-reply-row">
                        <button className="mono campus-report-btn campus-report-btn--accept" disabled={assignBusy} onClick={submitAssign}>
                          {assignBusy ? "SAVING…" : "REASSIGN ▶"}
                        </button>
                        <button className="mono campus-report-btn campus-report-btn--cancel" disabled={assignBusy} onClick={() => { setAssignOpen(false); setAssignError(null); }}>
                          CANCEL
                        </button>
                      </div>
                      {assignError && <span className="incident-reply-error campus-report-error mono" role="alert">{assignError}</span>}
                    </div>
                  )}
                </div>
              )}
              {lifecycleError && (
                <span className="incident-reply-error campus-report-error mono" role="alert">
                  {lifecycleError}
                </span>
              )}

              {/* ── Communication: message the reporter on Telegram ──────── */}
              {replyTgDone ? (
                <span className="campus-report-reply-sent mono" role="status">
                  {replyTgDone.inGroupSent && "✓ sent to Telegram group"}
                  {replyTgDone.inGroupSent && replyTgDone.dmSent && " · "}
                  {replyTgDone.dmSent && "✓ DM'd the reporter"}
                  {!replyTgDone.inGroupSent && !replyTgDone.dmSent && "queued (bot couldn't deliver yet)"}
                </span>
              ) : replyTgOpen ? (
                <div className="campus-report-reply-form">
                  <span className="eyebrow mono">Message {incident.nickname ?? "the reporter"} on Telegram</span>
                  <textarea
                    className="campus-report-reply-input mono"
                    placeholder="Progress, a question, or an issue — the reporter sees it in Telegram."
                    aria-label="Telegram message to reporter"
                    value={replyTgText}
                    onChange={(e) => setReplyTgText(e.target.value)}
                    maxLength={3500}
                    rows={3}
                  />
                  <div className="campus-report-reply-row">
                    <button className="mono campus-report-btn campus-report-btn--send" disabled={replyTgBusy} onClick={sendTelegramReply}>
                      {replyTgBusy ? "SENDING…" : "SEND ▶"}
                    </button>
                    <button className="mono campus-report-btn campus-report-btn--cancel" disabled={replyTgBusy} onClick={() => { setReplyTgOpen(false); setReplyTgError(null); }}>
                      CANCEL
                    </button>
                  </div>
                  {replyTgError && (
                    <span className="incident-reply-error campus-report-error mono" role="alert">{replyTgError}</span>
                  )}
                </div>
              ) : (
                <button
                  className="mono campus-report-btn campus-report-btn--reply"
                  onClick={() => { setReplyTgOpen(true); setReplyTgDone(null); }}
                  title="Send a message the reporter will see on Telegram"
                >
                  💬 MESSAGE REPORTER ▶
                </button>
              )}
            </>
          )}
        </div>
      )}

      <footer className="incident-card-foot">
        <span className="caption mono incident-foot-meta">
          {incident.reporterPlatform === "city-reporter" ? "City Reporter · " : isCampusReport ? "Telegram campus · " : ""}
          {isCampusReport ? "editable work log" : `tenant ${incident.tenantId ?? "(unscoped)"}`}
        </span>
        {!replyOpen && (
          <button
            className="mono incident-reply-btn"
            onClick={() => setReplyOpen(true)}
            title={
              isCampusReport
                ? "Internal note — logged to the work trail, NOT sent to the reporter"
                : "Log a categorized internal response note — recorded in the audit trail"
            }
          >
            {isCampusReport ? "🔒 INTERNAL NOTE ▶" : "LOG RESPONSE NOTE ▶"}
          </button>
        )}
      </footer>

      {replyOpen && (
        <IncidentReplyForm
          kinds={REPLY_KINDS}
          replyKind={replyKind}
          replyText={replyText}
          replyBusy={replyBusy}
          replyError={replyError}
          onKindChange={setReplyKind}
          onTextChange={setReplyText}
          onSend={isCampusReport ? sendCampusLog : sendReply}
          onCancel={() => { setReplyOpen(false); setReplyError(null); }}
        />
      )}
    </aside>
  );
}

export const IncidentCard = memo(IncidentCardImpl);
